/**
 * useCorrectionWatch — branche `correctionWatch` sur le serveur, le temps du live : tant qu'une attente
 * quittée reste en route, un relevé toutes les 5 s (`pollActivityCorrection`, comme l'attente, sans les
 * journaux, toutes les activités surveillées en une requête). Une correction prête relit son activité une
 * fois (`getActivities`), pour que le compte la porte quand l'élève l'ouvre : la même relecture que la fin
 * de l'attente.
 *
 * L'écran du live pose l'avis (`notice`) et dit ce que l'élève a fait : la correction regardée (`seen`),
 * l'avis fermé ou suivi (`dismiss`).
 *
 * Le live fini (l'élève a quitté, ou la séance est close), le relevé s'arrête (`enabled`) : l'écran de
 * fin s'empile sur le live sans le démonter, et l'avis ne serait vu par personne.
 */
import { useCallback, useEffect, useReducer, useRef } from "react"
import { useDispatch } from "react-redux"

import { activitiesService } from "../../../data/services/activitiesService"
import { apiCall, type ApiService } from "../../shared/apiCall"
import { type EngineTelemetry } from "../../shared/telemetry"
import {
    correctionNotice,
    correctionWatchReducer,
    initialCorrectionWatch,
    watchedIds,
    type PolledCorrection,
    type WatchedCorrection,
} from "./correctionWatch"
import { CORRECTION_POLL_INTERVAL_MS } from "./useCorrectionPolling"

export type CorrectionWatchHandle = {
    /** La correction prête à annoncer, une seule à la fois. */
    notice: WatchedCorrection | null
    /** L'attente de cette correction est quittée avant elle, ou sautée en Dialogue : on la surveille. */
    watch: (correction: WatchedCorrection) => void
    /** L'élève la regarde déjà : elle ne s'annoncera pas. */
    seen: (id: number) => void
    /** L'avis est fermé, ou suivi : il ne revient pas. */
    dismiss: (id: number) => void
}

type Dispatch = (action: never) => unknown

export function useCorrectionWatch(
    telemetry: Pick<EngineTelemetry, "error" | "transition">,
    options: { enabled?: boolean } = {}
): CorrectionWatchHandle {
    const { enabled = true } = options
    const dispatch = useDispatch() as unknown as Dispatch
    const [state, send] = useReducer(correctionWatchReducer, initialCorrectionWatch)
    const ids = watchedIds(state)
    const idsKey = enabled ? ids.join(",") : ""
    const telemetryRef = useRef(telemetry)
    telemetryRef.current = telemetry

    useEffect(() => {
        if (idsKey === "") return undefined
        const timer = setInterval(() => {
            const at = Date.now()
            send({ type: "TICK", at })
            apiCall<PolledCorrection[]>(dispatch, activitiesService.pollActivityCorrection as ApiService, {
                urlParams: { activity_ids: idsKey },
            })
                .then((rows) => send({ type: "POLLED", rows: Array.isArray(rows) ? rows : [], at: Date.now() }))
                .catch((cause: unknown) => telemetryRef.current.error("correction_watch_poll", cause))
        }, CORRECTION_POLL_INTERVAL_MS)
        return () => clearInterval(timer)
    }, [idsKey, dispatch])

    const notice = enabled ? correctionNotice(state) : null
    const noticeId = notice?.id ?? null
    useEffect(() => {
        if (noticeId == null) return
        telemetryRef.current.transition("watching", "ready", "correction_ready_elsewhere", { activity_id: noticeId })
        apiCall(dispatch, activitiesService.getActivities as ApiService, {
            urlParams: { with_logs: 1, activity_ids: noticeId },
        }).catch((cause: unknown) => telemetryRef.current.error("correction_watch_refresh", cause))
    }, [noticeId, dispatch])

    return {
        notice,
        watch: useCallback((correction: WatchedCorrection) => send({ type: "LEFT", correction, at: Date.now() }), []),
        seen: useCallback((id: number) => send({ type: "SEEN", id }), []),
        dismiss: useCallback((id: number) => send({ type: "DISMISSED", id }), []),
    }
}
